import React, { FC, forwardRef, useEffect, useRef } from 'react';
import {
  CalendarPickerView,
  DatePicker,
  DatePickerProps,
} from '@mui/x-date-pickers';
import Container from 'components/container';

import {
  DATE_FORMAT_REQUEST,
  USER_BIRTH_DAY_DATE_FORMAT,
} from 'data/constants';
import TextField from 'components/TextField/TextField';
import moment from 'moment';
import { Margins } from 'components/shared/types';
import useForceUpdate from 'hooks/useForceUpdate';

export interface IDateSelector
  extends Omit<DatePickerProps<string | null, string | null>, 'renderInput'> {
  disabledFuture?: boolean;
  name: string;
  dateFormat?: string;
  onChange: (
    value: string | null | undefined,
    keyboardInputValue?: string | undefined
  ) => void;
  setError?: (error: string) => void;
  views?: readonly CalendarPickerView[] | undefined;
  mt?: Margins['mt'];
  error?: boolean;
  errorMsg?: string;
  showHeaderYear?: boolean;
}

const DateSelector: FC<IDateSelector> = forwardRef(function DateSelector(
  {
    label,
    error,
    views,
    value,
    name,
    dateFormat = USER_BIRTH_DAY_DATE_FORMAT,
    disabledFuture = false,
    errorMsg,
    onChange,
    setError = () => {},
    showHeaderYear = true,
    mt,
    ...rest
  },
  ref
) {
  // set anchor el for popper to attach to
  const anchorEl = useRef<null | Element>(null);
  if (!anchorEl.current) {
    anchorEl.current = document.querySelector(`input[name="${name}"]`);
  }

  const handleChange = (date: string | null) => {
    const value = moment(date).format(DATE_FORMAT_REQUEST);
    onChange(value);
  };

  // fix when the DatePicker is conditionally rendered and
  // it's not rendered properly on the first time
  // (only happens on production build)
  //TODO: investigate why this happens
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    forceUpdate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="text-field-container" mt={mt}>
      {label && <div className="text-field-container__label">{label}</div>}
      <div className="text-field-container__content">
        <DatePicker
          PaperProps={{
            className: !showHeaderYear ? 'hide-header-year' : '',
          }}
          PopperProps={{
            anchorEl: anchorEl.current,
          }}
          views={views}
          value={value}
          onChange={handleChange}
          inputFormat={dateFormat}
          disableFuture={disabledFuture}
          renderInput={textFieldProps => (
            <TextField
              {...textFieldProps}
              name={name}
              inputRef={ref}
              value={value}
              FormHelperTextProps={{
                className: 'text-field-container__helper-text',
              }}
              size="small"
              error={error}
              errorMsg={errorMsg}
              sx={{
                '& input::placeholder': {
                  textTransform: 'uppercase',
                },
              }}
            />
          )}
          {...rest}
        />
      </div>
    </Container>
  );
});

export default DateSelector;
